import config from '@/config';
import { routes } from '@/router';
import { startupRequests } from '@/startup/startup-diagnostics';
import { StartupError } from '@/startup/startup-status';
import { AuthStore, useAuthStore } from '@/stores/authStore';
import { useRoleCreateStore } from '@/stores/role-create-store';
import { PermissionModel, useRolePermissionStore } from '@/stores/role-permission-store';
import { NavItemDefinition } from '@scu/core-ui';
import { Ref } from 'vue';
import { ComposerTranslation } from 'vue-i18n';
import { RouteRecordRaw } from 'vue-router';
import { NonFoodMenuPaths } from '../constants/non-food-constants';
import { SchemePaths } from '../constants/scheme-constants';
import { SendMenuPaths } from '../constants/send-constants';
import {
  BusinessPartnerPaths,
  ExternalWarehousesPaths,
  NonFoodPaths,
  SchemeParameterPaths,
} from '../constants/settings-constants';
import { SupplierAssignmentPaths } from '../constants/supplier-assignment-constants';
import { hasParentId } from '../validation/type-guards';

export const Permission = {
  Invisible: { id: 0, name: 'Nicht sichtbar' },
  Read: { id: 1, name: 'Lesen' },
  Write: { id: 2, name: 'Schreiben' },
} as const;

export const DefaultPermission = Permission.Invisible.id;

export enum PermissionFunctionKey {
  System = 1,
  Einstellungen = 2,
  Schema = 3,
  Dispo = 4,
  Versand = 5,
  Rollen = 6,
  Berechtigungen = 7,
  Berichte = 8,
  NewsVerwaltung = 9,
  TechnischeParameter = 10,
  FachlicheParameter = 11,
  ManuelleAufrufe = 12,
  LogTabelle = 13,
  SchedulerTabelle = 14,
  SchemaParameter = 15,
  Geschäftspartner = 16,
  Außenlager = 17,
  NonFoodEinstellungen = 18,
  TaskManagement = 19,
  Mitteilungsboard = 20,
  Schemaübersicht = 21,
  NonFoodMenu = 22,
  EdiError = 23,
  FileTransferError = 24,
  SupplierAssignment = 25,
  VersandNonFood = 26,
  VersandFood = 27,
  DispoImport = 28,
}

type PermissionFunctionDefinition = {
  id: PermissionFunctionKey;
  pathRegex: RegExp;
  parentId?: PermissionFunctionKey | undefined;
};

export const PermissionFunction = {
  System: {
    id: PermissionFunctionKey.System,
    pathRegex: /^\/system/,
  },
  Rollen: {
    id: PermissionFunctionKey.Rollen,
    pathRegex: /^roles/,
    parentId: PermissionFunctionKey.System,
  },
  Berechtigungen: {
    id: PermissionFunctionKey.Berechtigungen,
    pathRegex: /^permissions/,
    parentId: PermissionFunctionKey.System,
  },
  Berichte: {
    id: PermissionFunctionKey.Berichte,
    pathRegex: /^reports/,
    parentId: PermissionFunctionKey.System,
  },
  NewsVerwaltung: {
    id: PermissionFunctionKey.NewsVerwaltung,
    pathRegex: /^news-administration/,
    parentId: PermissionFunctionKey.System,
  },
  TechnischeParameter: {
    id: PermissionFunctionKey.TechnischeParameter,
    pathRegex: /^technical-parameters/,
    parentId: PermissionFunctionKey.System,
  },
  FachlicheParameter: {
    id: PermissionFunctionKey.FachlicheParameter,
    pathRegex: /^business-parameters/,
    parentId: PermissionFunctionKey.System,
  },
  ManuelleAufrufe: {
    id: PermissionFunctionKey.ManuelleAufrufe,
    pathRegex: /^manual-calls/,
    parentId: PermissionFunctionKey.System,
  },
  LogTabelle: {
    id: PermissionFunctionKey.LogTabelle,
    pathRegex: /^loggings/,
    parentId: PermissionFunctionKey.System,
  },
  SchedulerTabelle: {
    id: PermissionFunctionKey.SchedulerTabelle,
    pathRegex: /^job-scheduler/,
    parentId: PermissionFunctionKey.System,
  },
  EdiError: {
    id: PermissionFunctionKey.EdiError,
    pathRegex: /^edi-error/,
    parentId: PermissionFunctionKey.System,
  },
  FileTransferError: {
    id: PermissionFunctionKey.FileTransferError,
    pathRegex: /^file-transfer-error/,
    parentId: PermissionFunctionKey.System,
  },
  DispoImport: {
    id: PermissionFunctionKey.DispoImport,
    pathRegex: /^dispo-import/,
    parentId: PermissionFunctionKey.System,
  },
  Einstellungen: {
    id: PermissionFunctionKey.Einstellungen,
    pathRegex: /^\/settings/,
  },
  SchemaParameter: {
    id: PermissionFunctionKey.SchemaParameter,
    pathRegex: /^scheme-parameter-/,
    parentId: PermissionFunctionKey.Einstellungen,
  },
  Geschäftspartner: {
    id: PermissionFunctionKey.Geschäftspartner,
    pathRegex: /^business-partner/,
    parentId: PermissionFunctionKey.Einstellungen,
  },
  Außenlager: {
    id: PermissionFunctionKey.Außenlager,
    pathRegex: /^external-warehouses/,
    parentId: PermissionFunctionKey.Einstellungen,
  },
  NonFoodEinstellungen: {
    id: PermissionFunctionKey.NonFoodEinstellungen,
    pathRegex: /^non-food-/,
    parentId: PermissionFunctionKey.Einstellungen,
  },
  TaskManagement: {
    id: PermissionFunctionKey.TaskManagement,
    pathRegex: /^\/taskmanagement/,
  },
  Schema: {
    id: PermissionFunctionKey.Schema,
    pathRegex: /^\/scheme/,
  },
  Schemaübersicht: {
    id: PermissionFunctionKey.Schemaübersicht,
    pathRegex: /^overview/,
    parentId: PermissionFunctionKey.Schema,
  },
  Mitteilungsboard: {
    id: PermissionFunctionKey.Mitteilungsboard,
    pathRegex: /^notice-board/,
    parentId: PermissionFunctionKey.Schema,
  },
  Dispo: {
    id: PermissionFunctionKey.Dispo,
    pathRegex: /^\/dispo/,
  },
  Versand: {
    id: PermissionFunctionKey.Versand,
    pathRegex: /^\/send/,
  },
  VersandFood: {
    id: PermissionFunctionKey.VersandFood,
    pathRegex: /^send-food/,
    parentId: PermissionFunctionKey.Versand,
  },
  VersandNonFood: {
    id: PermissionFunctionKey.VersandNonFood,
    pathRegex: /^send-non-food/,
    parentId: PermissionFunctionKey.Versand,
  },
  NonFoodMenu: {
    id: PermissionFunctionKey.NonFoodMenu,
    pathRegex: /^\/non-food/,
  },
  SupplierAssignment: {
    id: PermissionFunctionKey.SupplierAssignment,
    pathRegex: /^\/supplier-assignment/,
  },
} as const satisfies Record<string, PermissionFunctionDefinition>;

export const AdminRoleId = 1;
export const AdminUserRole = ['gateway-keyuser'];

/**
 * checks whether the user's roles include the given SIAM key
 *
 * @param userRoles - the user's roles as a string or an array of strings
 * @param requiredSiamKey - the SIAM key to check against
 * @returns true if the SIAM key is present in the user's roles
 */
export function userHasSiamKey(userRoles: string[], requiredSiamKey: string): boolean {
  return userRoles.includes(requiredSiamKey);
}

export async function determineUserPermissionKeyForFunction(
  permissionFunctionDef: PermissionFunctionDefinition
): Promise<number> {
  const functionId = permissionFunctionDef.id;
  const rolePermissionStore = useRolePermissionStore();

  // Local helper function to extract permissionKey
  function getPermissionKeyForRole(allPermissions: Ref<PermissionModel[]>, roleId: number): number | undefined {
    const permission = allPermissions.value.find((p) => p.functionId === functionId);
    const role = permission?.roles.find((r) => r.roleId === roleId);
    return role?.permissionKey;
  }

  // automation check
  if (window.navigator.webdriver) {
    return Permission.Write.id;
  }

  const allPermissions = await rolePermissionStore.getAllPermissions();

  // auth disabled → admin rights
  if (config.auth.disabled) {
    return getPermissionKeyForRole(allPermissions, AdminRoleId) ?? DefaultPermission;
  }

  // normal authentication with user roles
  const authStore = useAuthStore();
  const roleStore = useRoleCreateStore();
  const userRoles = authStore.userRoles;
  const allRoles = await roleStore.getAllRoles();

  let highestPermission: number = DefaultPermission;

  for (const role of allRoles.value.filter((a) => userHasSiamKey(userRoles, a.siamKey))) {
    const permission = getPermissionKeyForRole(allPermissions, role.roleId);
    if (permission && permission > highestPermission) {
      highestPermission = permission;
    }
  }
  return highestPermission;
}

export function checkOnWritePermission(permissionKey: number) {
  return permissionKey === Permission.Write.id;
}

export function checkOnWriteOrReadPermission(permissionKey: number) {
  return permissionKey !== Permission.Invisible.id;
}

export function determineFunctionVisibility(
  permissionFunctionDef: PermissionFunctionDefinition | undefined,
  authStore: AuthStore
): boolean {
  let isVisible = false;
  let relevantRoutes: RouteRecordRaw | undefined = undefined;
  const userRoles = config.auth.disabled ? AdminUserRole : authStore.userRoles;

  function getRelevantRoute(parentPathStart: RegExp | undefined, childPathStart: RegExp) {
    const systemRoute = routes.filter((route) => parentPathStart?.test(route.path)).at(0);

    return systemRoute?.children?.filter((route) => route !== undefined && childPathStart.test(route.path)).at(0);
  }

  if (userRoles && permissionFunctionDef) {
    if (permissionFunctionDef.parentId !== undefined) {
      const parentRoute = Object.values(PermissionFunction).find((f) => f.id == permissionFunctionDef.parentId);
      relevantRoutes = getRelevantRoute(parentRoute?.pathRegex, permissionFunctionDef.pathRegex);
    } else {
      relevantRoutes = routes.filter((route) => permissionFunctionDef.pathRegex.test(route.path)).at(0);
    }

    isVisible = userRoles.some((role) => {
      return relevantRoutes?.meta?.requiredGroups?.includes(role);
    });
  }
  return isVisible;
}

export function determineNavItems(authStore: AuthStore, t: ComposerTranslation): NavItemDefinition[] {
  const initialNavItems = [
    determineFunctionVisibility(PermissionFunction.TaskManagement, authStore)
      ? { label: t('menu.taskmanagement'), href: '/taskmanagement' }
      : { label: '', href: '' },
    determineFunctionVisibility(PermissionFunction.Schema, authStore)
      ? {
          label: t('menu.scheme'),
          href: SchemePaths.Root,
          children: [
            determineFunctionVisibility(PermissionFunction.Schemaübersicht, authStore)
              ? {
                  label: `${t('pages.scheme.table.pageName')} ${t('pages.scheme.table.overview')}`,
                  href: SchemePaths.Overview,
                }
              : null,
            determineFunctionVisibility(PermissionFunction.Mitteilungsboard, authStore)
              ? { label: t('pages.scheme.noticeBoard.pageName'), href: SchemePaths.NoticeBoard }
              : null,
          ].filter(Boolean) as NavItemDefinition[],
        }
      : { label: '', href: '' },
    determineFunctionVisibility(PermissionFunction.SupplierAssignment, authStore)
      ? { label: t('menu.supplierAssignment'), href: SupplierAssignmentPaths.Root }
      : { label: '', href: '' },
    determineFunctionVisibility(PermissionFunction.Dispo, authStore)
      ? { label: t('menu.dispo'), href: '/dispo' }
      : { label: '', href: '' },
    determineFunctionVisibility(PermissionFunction.Versand, authStore)
      ? {
          label: t('menu.send'),
          href: '/send',
          children: [
            determineFunctionVisibility(PermissionFunction.VersandFood, authStore)
              ? { label: t('pages.send.food.pageName'), href: SendMenuPaths.SendMonitorFood }
              : null,
            determineFunctionVisibility(PermissionFunction.VersandNonFood, authStore)
              ? { label: t('pages.send.nonfood.pageName'), href: SendMenuPaths.SendMonitorNonFood }
              : null,
          ].filter(Boolean) as NavItemDefinition[],
        }
      : { label: '', href: '' },
    determineFunctionVisibility(PermissionFunction.NonFoodMenu, authStore)
      ? {
          label: t('menu.nonFood'),
          href: '/non-food',
          children: [
            {
              label: t('pages.nonFood.logisticPlanner.pageName'),
              href: NonFoodMenuPaths.DispoWeeks,
            },
            { label: t('pages.nonFood.change.pageName'), href: NonFoodMenuPaths.Changes },
            { label: t('pages.nonFood.importFeedBack.pageName'), href: NonFoodMenuPaths.ImportFeedback },
          ] as NavItemDefinition[],
        }
      : { label: '', href: '' },
    determineFunctionVisibility(PermissionFunction.Einstellungen, authStore)
      ? {
          label:
            String.fromCharCode(160) +
            String.fromCharCode(160) +
            String.fromCharCode(160) +
            String.fromCharCode(160) +
            String.fromCharCode(160) +
            String.fromCharCode(160) +
            t('menu.settings'),
          href: '/settings',
          children: [
            determineFunctionVisibility(PermissionFunction.SchemaParameter, authStore)
              ? { label: t('pages.settings.schemeParameter.name'), href: SchemeParameterPaths.BasicData }
              : null,
            determineFunctionVisibility(PermissionFunction.Geschäftspartner, authStore)
              ? { label: t('pages.settings.businessPartner.name'), href: BusinessPartnerPaths[0] }
              : null,
            determineFunctionVisibility(PermissionFunction.Außenlager, authStore)
              ? { label: t('pages.settings.externalWarehouses.name'), href: ExternalWarehousesPaths[0] }
              : null,
            determineFunctionVisibility(PermissionFunction.NonFoodEinstellungen, authStore)
              ? { label: t('pages.settings.nonFood.name'), href: NonFoodPaths.DispoWeeks }
              : null,
          ].filter(Boolean) as NavItemDefinition[],
        }
      : { label: '', href: '' },
  ];
  const filteredNavItems: NavItemDefinition[] = initialNavItems.filter((navItem) => navItem.label && navItem.href);
  return filteredNavItems;
}

export async function setRequiredGroupsForRoutes(routes: RouteRecordRaw[]) {
  const roleStore = useRoleCreateStore();
  startupRequests.begin('Rechte', '/role/all');
  const allRoles = (await roleStore.getAllRoles()).value;
  startupRequests.end('/role/all');
  const rolePermissionStore = useRolePermissionStore();
  startupRequests.begin('Rechte', '/permission');
  const allPermissions = await rolePermissionStore.getAllPermissions();
  startupRequests.end('/permission');
  const hiddenKey = Permission.Invisible.id;

  const loadError = roleStore.error ?? rolePermissionStore.error;
  if (loadError) throw new StartupError('Rechteermittlung', loadError);

  for (const permission of allPermissions.value) {
    const permissionFunctionId = permission.functionId;
    const siamKeysOfRolesWithAccessPermission: string[] = [];
    const rolesWithAccessPermission = permission.roles.filter((role) => role.permissionKey !== hiddenKey);

    const childs = allPermissions.value.filter((role) => role.parentId === permission.functionId);

    const childRolesWithAccessPermission = new Set(
      childs.flatMap((role) => role.roles.filter((r) => r.permissionKey !== hiddenKey).map((r) => r.siamKey))
    );

    if (childs.length > 0) {
      childRolesWithAccessPermission.forEach((siamKey) => siamKeysOfRolesWithAccessPermission.push(siamKey));
    } else {
      rolesWithAccessPermission.forEach((role) => siamKeysOfRolesWithAccessPermission.push(role.siamKey));
    }

    const permissionFunctionDef = Object.values(PermissionFunction).find((f) => f.id == permissionFunctionId);

    if (permissionFunctionDef && hasParentId(permissionFunctionDef)) {
      const parentRoute = Object.values(PermissionFunction).find((f) => f.id == permissionFunctionDef.parentId);
      assignRequiredGroups(
        routes,
        parentRoute?.pathRegex,
        siamKeysOfRolesWithAccessPermission,
        permissionFunctionDef?.pathRegex
      );
    } else {
      assignRequiredGroups(routes, permissionFunctionDef?.pathRegex, siamKeysOfRolesWithAccessPermission);
    }
  }

  // only users with any gateway role have access to the homepage
  for (const role of allRoles) {
    const siamKey = role.siamKey;
    const startRoute = routes.find((route) => (route.path = '/'));
    startRoute?.meta?.requiredGroups?.push(siamKey);
  }

  function assignRequiredGroups(
    routes: RouteRecordRaw[],
    pathStart: RegExp | undefined,
    siamKeys: string[],
    pathChild: RegExp | null = null
  ) {
    if (pathStart !== undefined) {
      if (pathChild != null) {
        const parentRoute = routes.filter((route) => pathStart.test(route.path)).at(0);
        const childRoutes = parentRoute?.children?.filter((route) => route !== undefined && pathChild.test(route.path));

        childRoutes?.forEach((childRoute) => childRoute?.meta?.requiredGroups?.push(...siamKeys));
      } else {
        routes.forEach((route) => {
          if (pathStart.test(route.path)) {
            route.meta?.requiredGroups?.push(...siamKeys);
          }
        });
      }
    }
  }
}
